import { assert, NormalizedNumber, UnixTimestamp } from '@sparkdotfi/common-universal'
import { UseMutationResult, useMutation } from '@tanstack/react-query'
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts'

import { createSandbox, getChainIdWithPrefix } from './createSandbox'
import { UseCloneVnetExtensionResult, useCloneVnetExtension } from './useCloneVnetExtension'
import { SANDBOX_NETWORKS_CHAIN_ID_PREFIX } from '@/config/consts'
import { UseInputAddressExtensionResult, useInputAddressExtension } from '@/domain/hooks/useInputAddressExtension'
import { cloneVnet } from '@/domain/sandbox/cloneVnet'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useStore } from '@/domain/state'
import { useCloseDialog } from '@/domain/state/dialogs/hooks'
import { useWatchWalletModeActions } from '@/domain/wallet/useWatchWalletModeActions'

export type UseSandboxMutationResult = Omit<UseMutationResult<void, Error, void, unknown>, 'mutate'> & {
  startSandbox: () => void
}

export interface UseSandboxDialogResult extends UseSandboxMutationResult {
  isInSandbox: boolean
  activateButtonEnabled: boolean
  inputAddressExtension: UseInputAddressExtensionResult
  cloneVnetExtension: UseCloneVnetExtensionResult
}

export function useSandboxDialog(): UseSandboxDialogResult {
  const sandboxConfig = useStore((state) => state.appConfig.sandbox)
  const { setNetwork } = useStore((state) => state.sandbox)

  const { disableWatchWalletMode } = useWatchWalletModeActions()

  const { isInSandbox } = useSandbox()
  const inputAddressExtension = useInputAddressExtension()
  const cloneVnetExtension = useCloneVnetExtension()

  const closeDialog = useCloseDialog()

  assert(sandboxConfig, 'It seems that sandbox feature is not enabled.')

  // eslint-disable-next-line func-style
  const startSandboxAsync = async (): Promise<void> => {
    disableWatchWalletMode()

    const createdAt = new Date()
    const forkChainId = getChainIdWithPrefix(SANDBOX_NETWORKS_CHAIN_ID_PREFIX, UnixTimestamp.fromDate(createdAt))

    if (inputAddressExtension.enabled && inputAddressExtension.address) {
      const userAddress = inputAddressExtension.address

      const forkUrl = await (async () => {
        if (cloneVnetExtension.enabled && cloneVnetExtension.srcContainerId) {
          const { rpcUrl } = await cloneVnet({ srcContainerId: cloneVnetExtension.srcContainerId })
          return rpcUrl
        }

        return await createSandbox({
          originChainId: sandboxConfig.originChainId,
          forkChainId,
          userAddress,
          mintBalances: { etherAmt: NormalizedNumber(0), tokenAmt: NormalizedNumber(0), tokens: {} },
          setupAirdropForUser: false,
        })
      })()

      setNetwork({
        originChainId: sandboxConfig.originChainId,
        forkChainId,
        forkUrl,
        createdAt,
        name: sandboxConfig.chainName,
        ephemeralAccountAddress: userAddress,
        sandboxEnabled: true,
      })

      return
    }

    const privateKey = generatePrivateKey()
    const account = privateKeyToAccount(privateKey)

    const forkUrl = await createSandbox({
      originChainId: sandboxConfig.originChainId,
      forkChainId,
      userAddress: account.address,
      mintBalances: sandboxConfig.mintBalances,
      setupAirdropForUser: true,
    })
    const sandboxStoreState = {
      originChainId: sandboxConfig.originChainId,
      forkChainId,
      forkUrl,
      createdAt,
      name: sandboxConfig.chainName,
      ephemeralAccountPrivateKey: privateKey,
      ephemeralAccountAddress: account.address,
      sandboxEnabled: true,
    }
    setNetwork(sandboxStoreState)
  }

  async function startSandbox(): Promise<void> {
    try {
      await startSandboxAsync()
    } catch (e: any) {
      // biome-ignore lint/suspicious/noConsole: debugging
      console.error(e)
      throw new Error(`Could not enter Sandbox: ${e.message}`)
    }
  }

  const startSandboxMutation = useMutation({
    mutationFn: startSandbox,
    onSuccess: closeDialog,
  })

  return {
    isInSandbox,
    activateButtonEnabled:
      (inputAddressExtension.enabled ? inputAddressExtension.address !== null : true) &&
      (cloneVnetExtension.enabled ? cloneVnetExtension.srcContainerId !== null : true),
    inputAddressExtension,
    cloneVnetExtension,
    startSandbox: startSandboxMutation.mutate,
    ...startSandboxMutation,
  }
}
